import React, { useEffect } from "react";
import "./App.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { persistThemeMode } from "./theme";
import Layout from "./components/Layout/Layout";
import Dashboard from "./pages/Dashboard";
import GamesAnalytics from "./pages/monitor/GamesAnalytics";
import Revenue from "./pages/monitor/Revenue";
import Acquisition from "./pages/monitor/Acquisition";
import UtmAttribution from "./pages/monitor/UtmAttribution";
import Retention from "./pages/monitor/Retention";
import PwaStats from "./pages/monitor/PwaStats";
import Players360 from "./pages/audience/Players360";
import Segments from "./pages/audience/Segments";
import Risk from "./pages/operate/Risk";
import Marketing from "./pages/operate/Marketing";
import ErrorLog from "./pages/operate/ErrorLog";
import AIInsights from "./pages/intelligence/AIInsights";
import Audit from "./pages/intelligence/Audit";

// Auth / public screens
import Login from "./screens/Login";
import ForgotPassword from "./screens/ForgotPassword";
import ResetPassword from "./screens/ResetPassword";
import Joinnow from "./screens/Joinnow";
import Demo from "./screens/Demo";
import LoadingIndicator from "./components/Spinner";

// Players / Users
import UserList from "./screens/User/UserList";
import ActionList from "./screens/User/ActionList";
import AccessList from "./screens/User/AccessList";
import UserDetails from "./screens/User/UserList/UserDetails";
import BlockDomain from "./screens/User/BlockDomain";

// Casino
import Provider from "./screens/Casino/Provider";
import Menu from "./screens/Casino/Menu";
import Category from "./screens/Casino/Category";
import FeatureGames from "./screens/Casino/FeatureGames";
import CoinPackage from "./screens/Casino/CoinPackage";
import Games from "./screens/Casino/Games";
import Promo from "./screens/Casino/Promo";
import RedemptionClaims from "./screens/Casino/RedemptionClaims";
import RedemptionDetails from "./screens/Casino/RedemptionClaims/redemDetails";
import ReferFriendDailyClaim from "./screens/Casino/ReferFriendDailyClaim";
import WheelofFortune from "./screens/WheelofFortune";
import Lottery from "./screens/Lottery";

// Finance / Transactions
import Transfer from "./screens/Transfer";
import Payments from "./screens/Payments";
import Withdrawals from "./screens/Withdrawals";
import ExchangeRate from "./screens/ExchangeRate";

// Promotions & Marketing
import Loyalty from "./screens/Loyalty";
import Banner from "./screens/Banner";
import MarketingPopup from "./screens/MarketingPopup";
import MonthlyPromotions from "./screens/MontlyPromotions";
import PromotionNavigation from "./screens/PromotionsNavigation";
import MailInList from "./screens/MailInList";
import PopupOffers from "./screens/PopupOffers";
import PromoCampaign from "./screens/PromoCampaign";
import Survey from "./screens/Survey";
import OfferPost from "./OfferPost";

// Engagement
import HappyHour from "./screens/HappyHour";
import HappyHourEarnings from "./screens/HappyHourEarnings";
import StreakTypes from "./screens/StreakTypes";
import TierMultifactor from "./screens/TierMultifactor";

import { Actions } from "./redux/Actions/Actions";
import { axiosInstance } from "./Utility/axiosInstances";

// Safely read + parse a sessionStorage value (guards against null / the
// literal string "undefined" which would make JSON.parse throw).
function readSession(key) {
  const raw = sessionStorage.getItem(key);
  if (raw == null || raw === "undefined" || raw === "") return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function RequireAuth() {
  const User = readSession("user");
  return User != null ? <Layout /> : <Navigate to="/login" replace />;
}

const App = () => {
  const { auth } = useSelector((state) => state);
  const dispatch = useDispatch();

  useEffect(() => {
    const Session = readSession("session");
    const User = readSession("user");
    if (Session != null) {
      dispatch({ type: Actions.LOGIN, data: { user: User } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global 401 handling (ported from legacy App.js)
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        localStorage.clear();
        persistThemeMode(); // a device preference, not session data
        window.alert(
          "You are currently logged in on another device. Please login again to continue."
        );
        dispatch({ type: Actions.LOGOUT });
        sessionStorage.clear();
        window.location.reload();
      }
      return error;
    }
  );

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/ForgotPassword" element={<ForgotPassword />} />
      <Route path="/Join-Now" element={<Joinnow />} />
      <Route path="/Demo/userId=:id/password=:pass" element={<Demo />} />
      <Route path="/Spinner" element={<LoadingIndicator />} />

      {/* Authenticated app — everything inside the new Layout shell */}
      <Route element={<RequireAuth />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/Home" element={<Dashboard />} />

        {/* Monitor */}
        <Route path="/monitor/games" element={<GamesAnalytics />} />
        <Route path="/monitor/revenue" element={<Revenue />} />
        <Route path="/monitor/acquisition" element={<Acquisition />} />
        <Route path="/monitor/utm-attribution" element={<UtmAttribution />} />
        <Route path="/monitor/retention" element={<Retention />} />
        <Route path="/monitor/pwa" element={<PwaStats />} />

        {/* Audience */}
        <Route path="/audience/players" element={<Players360 />} />
        <Route path="/audience/segments" element={<Segments />} />

        {/* Operate */}
        <Route path="/operate/risk" element={<Risk />} />
        <Route path="/operate/marketing" element={<Marketing />} />
        <Route path="/operate/error-log" element={<ErrorLog />} />

        {/* Intelligence */}
        <Route path="/intelligence/ai-insights" element={<AIInsights />} />
        <Route path="/intelligence/audit" element={<Audit />} />

        {/* Players */}
        <Route path="/UserList" element={<UserList />} />
        <Route path="/block-domain" element={<BlockDomain />} />
        <Route path="/ActionList" element={<ActionList />} />
        <Route path="/AccessList" element={<AccessList />} />
        <Route path="/UserDetails/:userId" element={<UserDetails />} />

        {/* Casino */}
        <Route path="/provider" element={<Provider />} />
        <Route path="/menu" element={<Menu />} />
        <Route path="/category" element={<Category />} />
        <Route path="/featureGames" element={<FeatureGames />} />
        <Route path="/coinPackage" element={<CoinPackage />} />
        <Route path="/games" element={<Games />} />
        <Route path="/promo" element={<Promo />} />
        <Route path="/redemptionClaims" element={<RedemptionClaims />} />
        <Route path="/redemption-details" element={<RedemptionDetails />} />
        <Route path="/config-list" element={<ReferFriendDailyClaim />} />
        <Route path="/wheel-of-fortune" element={<WheelofFortune />} />
        <Route path="/lottery" element={<Lottery />} />

        {/* Finance */}
        <Route path="/Transfer" element={<Transfer />} />
        <Route path="/Payments" element={<Payments />} />
        <Route path="/withdrawals" element={<Withdrawals />} />
        <Route path="/exchange-rate" element={<ExchangeRate />} />

        {/* Promotions */}
        <Route path="/loyalty" element={<Loyalty />} />
        <Route path="/Banner" element={<Banner />} />
        <Route path="/MarketingPopup" element={<MarketingPopup />} />
        <Route path="/popup-offers" element={<PopupOffers />} />
        <Route path="/promo-campaigns" element={<PromoCampaign />} />
        <Route path="/survey" element={<Survey />} />
        <Route path="/all-promotions" element={<PromotionNavigation />} />
        <Route path="/monthly-promotions" element={<MonthlyPromotions />} />
        <Route path="/mailIn-list" element={<MailInList />} />
        <Route path="/offer-list" element={<OfferPost />} />

        {/* Engagement */}
        <Route path="/happy-hours" element={<HappyHour />} />
        <Route path="/happy-hour-earnings" element={<HappyHourEarnings />} />
        <Route path="/streak-types" element={<StreakTypes />} />
        <Route path="/tier-multifactor" element={<TierMultifactor />} />

        <Route path="/Reset-Password" element={<ResetPassword />} />
      </Route>

      <Route
        path="*"
        element={<Navigate to={auth?.isAuthenticated ? "/" : "/login"} replace />}
      />
    </Routes>
  );
};

export default App;
